import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defaultNotifier } from 'functions/notificationHandler';
import Card from 'components/Detail/Card';
import { isEqual } from 'lodash';
import {
  DEFAULT_CARTESIAN_CHART_CONFIG,
  DEFAULT_CARD_CHART_CONFIG,
  getCardChartConfig,
  getCartesianChartConfig,
  formatYAxisLabel,
  getActualTimeRange,
  getChartRenderRequirements,
  chartDotMenu
} from './utils'
import classNames from 'classnames';
import ChartDotMenu from './ChartDotMenu';
import { ChartLoader } from './ChartLoader';
import { hexToRgba } from 'utils/common';
import { rawChartData, previewRawChartData } from '../../../../../api/dashboard';
import { CHART_DEFAULT_VALUES } from '../chartConfigurationsV2'
import { BLOCK, WARN } from './constants';

class CardChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartData: {
        groupBy: [],
        data: []
      },
      metricColumns: [],
      validData: false,
      dataErrorMessage: '',
      dataWarnMessage: '',
      cardConfig: { ...DEFAULT_CARD_CHART_CONFIG },
      isChartDataLoading: false,

      chartConfig: { ...CHART_DEFAULT_VALUES },
      hasMenu: false
    }
  }

  // eslint-disable-next-line
  componentWillReceiveProps(nextprops) {
    if (!isEqual(this.props.isDashboardInitialized, nextprops.isDashboardInitialized)) {
      if (nextprops.isPreview) {
        return this.setPreviewData()
      }
      if (nextprops.isDashboardInitialized) {
        return this.setData()
      }
    }

    if (nextprops.errors) {
      defaultNotifier(nextprops.errors, this.props);
    }
  }

  async componentDidMount() {
    const isOldChartConfig = typeof this.props.chart?.configuration === 'object' && this.props.chart?.configuration.hasOwnProperty('basicFields')
    this.setState({ hasMenu: !isOldChartConfig })

    if (!this.props.isPreview && !isOldChartConfig) {
      const chartConfig = JSON.parse(this.props.chart?.configuration)
      this.setState({ chartConfig: chartConfig }, () => { 
        this.setRawChartData()
      })
    }

    if (this.props.isPreview) {
      this.setState({ chartConfig: this.props.chartValues, hasMenu: false }, () => {
        this.setPreviewData()
      })
    }
  }

  async componentDidUpdate(prevProps) {
    if (this.props.isPreview) {
      const currentChartValues = this.props.chartValues
      const prevChartValues = this.state.chartConfig

      const currentDataStreams = this.props.dataStreams
      const prevDataStreams = prevProps.dataStreams
      const currentChart = this.props.chart
      const prevChart = prevProps.chart
      const currentPreviewParams = this.props.previewParams
      const prevPreviewParams = prevProps.previewParams

      // Chart type change - trigger refresh
      if (!isEqual(this.props.chart?.type, prevProps.chart?.type)) {
        return this.setPreviewData()
      }

      // RefreshPreview toggle - trigger refresh (for chart type changes)
      if (!isEqual(this.props.refreshPreview, prevProps.refreshPreview)) {
        return this.setPreviewData()
      }

      // Data streams change - trigger refresh
      if (!isEqual(currentDataStreams, prevDataStreams)) {
        return this.setPreviewData()
      }

      // Chart values change - update config and potentially refresh
      if (!isEqual(currentChartValues, prevChartValues)) {
        this.setState({ chartConfig: { ...currentChartValues } }, () => {
          // Check if specific properties changed that require refresh
          if (!isEqual(currentChartValues.devices, prevChartValues.devices) ||
            !isEqual(currentChartValues.groupBy, prevChartValues.groupBy)) {
            return this.setPreviewData()
          }
        })
      }

      // Devices data change - trigger refresh
      if (!isEqual(prevProps.devicesData, this.props.devicesData)) {
        return this.setPreviewData()
      }

      // Chart or preview params change - trigger refresh
      if (!isEqual(currentChart, prevChart) || !isEqual(currentPreviewParams, prevPreviewParams)) {
        return this.setPreviewData()
      }

      // Chart values change for new version - update cartesian config only
      // if (!isEqual(this.props.chartValues, prevProps.chartValues) && this.props.isNewVersion) {
      //   return this.setState({
      //     cartesianConfig: mapChartValuesToCartesianConfig(this.props.chartValues)
      //   })
      // }

      // Legacy refresh preview handling for old version
      if (this.props.refreshPreview && !this.props.isNewVersion) {
        return this.setState({
          cartesianConfig: getCartesianChartConfig(this.props.chart.configuration)
        })
      }
    }

    if (!isEqual(this.props.devicesData, prevProps.devicesData)) {
      return this.setPreviewData()
    }

    const prevIsDashboardInitialized = prevProps.isDashboardInitialized
    const actualIsDashboardInitialized = this.props.isDashboardInitialized
    if ((prevIsDashboardInitialized !== actualIsDashboardInitialized) && actualIsDashboardInitialized) {
      this.setData()
    }
  }

  setPreviewData = async () => {
    const { chart, previewParams, dashboardTimeRange, isPreview, chartValues, isDefaultTime } = this.props

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview)

    try {
      // const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      // if (hasBlockingIssues) {
      //   return this.setState({ isChartDataLoading: false })
      // }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  setData = async () => {
    const { chart, dashboardTimeRange, dashboardDevices } = this.props
    this.setState({ isChartDataLoading: true })

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange)

    try {
      const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      if (hasBlockingIssues) {
        return this.setState({ isChartDataLoading: false })
      }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  // Prevent unnecessary re-renders (e.g., from parent scroll)
  shouldComponentUpdate(nextProps, nextState) {
    return (
      !isEqual(this.props.height, nextProps.height) ||
      !isEqual(this.props.chartType, nextProps.chartType) ||
      !isEqual(this.props.chartValues, nextProps.chartValues) ||
      !isEqual(this.props.dataStreams, nextProps.dataStreams) ||
      !isEqual(this.props.previewParams, nextProps.previewParams) ||
      !isEqual(this.props.devicesData, nextProps.devicesData) ||
      !isEqual(this.props.chart, nextProps.chart) ||
      this.props.isEditMode !== nextProps.isEditMode ||
      !isEqual(this.state.chartData, nextState.chartData) ||
      !isEqual(this.state.chartConfig, nextState.chartConfig) ||
      this.state.isChartDataLoading !== nextState.isChartDataLoading
    );
  }

  checkChartRenderRequirements = (timeRange) => {
    const requirements = getChartRenderRequirements(this.state.chartConfig, timeRange)

    const warnMessages = requirements[WARN].map(r => r.message).join('\n')
    const blockMessages = requirements[BLOCK].map(r => r.message).join('\n')
    const hasWarningIssues = requirements[BLOCK].length > 0
    const hasBlockingIssues = requirements[BLOCK].length > 0

    if (hasWarningIssues) {
      this.setState({ dataWarnMessage: warnMessages })
    }

    if (hasBlockingIssues) {
      this.setState({
        validData: false,
        dataErrorMessage: blockMessages,
        chartData: { groupBy: [], data: [] }
      })
    }

    return {
      hasBlockingIssues
    }
  }

  setChartData = (data, chart) => {
    const { groupBy, metricsData, metricColumns } = data
    const cardConfig = getCardChartConfig(chart.configuration)
    const chartData = {
      groupBy: [...groupBy],
      data: [...metricsData]
    }

    return this.setState({
      chartData: chartData,
      metricColumns: metricColumns,
      cardConfig: cardConfig,
      dataErrorMessage: '',
      validData: true
    })
  }

  getDisplayedData = () => {
    const { chartData, cardConfig, chartConfig } = this.state

    if (chartData.data.length === 0) {
      return 'No Data'
    }

    // Prefer new V2 chartConfig options; fallback to legacy cardConfig
    const format = chartConfig?.format ?? cardConfig?.format ?? 'Default'
    const decimal = chartConfig?.decimal ?? cardConfig?.decimal ?? 0
    const prefix = chartConfig?.prefix ?? cardConfig?.prefix ?? ''
    const suffix = chartConfig?.suffix ?? cardConfig?.suffix ?? ''

    const value = this.getCalculatedChartData()
    const formatted = formatYAxisLabel(value, format, decimal)

    // Apply prefix/suffix around the formatted value
    return `${prefix} ${formatted} ${suffix}`.trim()
  }

  getLabel = () => {
    return this.state.chartConfig?.dataStreamCard?.streamLabel.value
  }

  getOverrideData = () => {
    const { chartData, chartConfig } = this.state
    const data = this.getCalculatedChartData()

    if (chartData.data.length === 0) {
      return ''
    }

    if (chartConfig?.colorTiers?.customConfigs?.length > 0 && chartConfig?.enableThreshold) {
      const tiers = chartConfig.colorTiers.customConfigs;
      let labelText = tiers[0].text
      let overrideValue = ''

      if (chartConfig.allThresholdType === 'absolute') {
        for (const tier of tiers) {
          if (parseFloat(data) >= parseFloat(tier.value)) {
            overrideValue = tier.text
          }
        }
        return overrideValue
      }
      if (chartConfig.allThresholdType === 'percent') {
        for (const tier of tiers) {
          if (parseFloat(data) >= parseFloat(tier.value)) {
            overrideValue = tier.text
          }
        }
        return overrideValue
      }
      if (chartConfig.allThresholdType === 'label') {
        const displayedValue = this.getDisplayedData();
        const findTier = tiers.find(tier => (tier && tier?.value && tier?.value) === displayedValue?.trim())
        console.log('ddfindTier', findTier)

        if (findTier) {
          overrideValue = findTier.text
          return overrideValue
        }

      }
      return overrideValue
    }
  }

  getCalculatedChartData = () => {
    const { chartData } = this.state

    if (chartData.data.length === 0) {
      return 0
    }

    const firstItem = chartData?.data?.[0] || {}
    let numericValue = null

    // New v2 format: values object with numeric streams
    if (firstItem && typeof firstItem === 'object' && firstItem.values && typeof firstItem.values === 'object') {
      const allValues = Object.values(firstItem.values)
        .filter(val => val != null && !isNaN(val))
        .map(val => parseFloat(val) || 0)

      if (allValues.length > 0) {
        numericValue = allValues.reduce((sum, val) => sum + val, 0)
      }
    }

    // Legacy format: find first numeric field (excluding time fields)
    if (numericValue == null && firstItem && typeof firstItem === 'object') {
      for (const [key, value] of Object.entries(firstItem)) {
        if (key !== 'bucketed_time' && key !== 'time' && value != null && !isNaN(value)) {
          numericValue = parseFloat(value) || 0
          break
        }
      }
    }

    // As a final fallback (non-numeric content), return a label-like string
    if (numericValue == null) {
      const labelParts = Object.values(firstItem).filter(v => v != null)
      return labelParts.length ? String(labelParts[0]) : 0
    }

    return numericValue
  }

  getCardColorTierByData = () => {
    const { cardConfig, chartConfig } = this.state;
    const { chartValues } = this.props;
    const data = this.getCalculatedChartData()

    if (chartConfig?.colorTiers?.customConfigs?.length > 0 && chartConfig?.enableThreshold) {
      const tiers = chartConfig.colorTiers.customConfigs;
      let lastActiveColor = tiers[0].color

      if (chartConfig.allThresholdType === 'absolute') {
        for (const tier of tiers) {
          if (data >= parseFloat(tier.value)) {
            lastActiveColor = tier.color
          }
        }
        return lastActiveColor
      }
      if (chartConfig.allThresholdType === 'percent') {
        for (const tier of tiers) {
          if (data >= parseFloat(tier.value)) {
            lastActiveColor = tier.color
          }
        }

        return lastActiveColor
      }

      if (chartConfig.allThresholdType === 'label') {
        const displayedValue = this.getDisplayedData()

        const findTier = tiers.find(tier => tier?.value === displayedValue?.trim())

        if (findTier) {
          lastActiveColor = findTier.color;

          return lastActiveColor
        }

        return '#2184f9'

      }

      return lastActiveColor
    } else {
      let lastColor = '#8b959e';
      lastColor = chartConfig?.dataStreamCard?.streamLabel?.color || lastColor
      return lastColor
    }
  }

  getDataProgressPercentage = () => {
    const { chartConfig } = this.state;

    const data = this.getCalculatedChartData();
    const minData = Number(chartConfig?.cardType?.range?.min ?? 0);
    const maxData = Number(chartConfig?.cardType?.range?.max ?? 100);

    // Prevent divide-by-zero and ensure valid range
    const denom = Math.max(1, maxData - minData);
    const raw = ((Number(data) - minData) / denom) * 100;
    const clamped = Math.max(0, Math.min(100, raw));

    return clamped;
  }

  getDeviceList = () => {
    const { chartValues, devicesData } = this.props

    if (chartValues?.devices && !!chartValues.devices.length) {
      return chartValues.devices
    }

    return devicesData?.map(item => item.name)
  }

  setRawChartData = async () => {
    const { chart, dashboardTimeRange, isPreview, chartValues, isNewVersion, isDefaultTime } = this.props
    const { chartConfig } = this.state

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview, true, chartConfig)

    let groupByMetrics = chartConfig?.groupBy?.metrics || []

    if (this.props.isPreview) {
      groupByMetrics = chartValues?.groupBy?.metrics || []
    }

    // Indicate loading immediately to avoid flashing empty/error state
    this.setState({ isChartDataLoading: true })

    if (!chartConfig?.dataStreamCard?.metric?.metrics.length) {
      if (!this.props.isPreview) {
        // Suppress error on dashboard initial load; wait for config to be ready
        return
      }
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: "Select a metric to display the preview",
        validData: false,
        isChartDataLoading: false
      })
    }

    if (this.props.sqlError) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: this.props.sqlError,
        validData: false
      })
    }

    this.setState({ isChartDataLoading: true })

    let data = {}

    const devices = this.getDeviceList();

    if (!devices.length) {
      return this.setState({ isChartDataLoading: false })
    }

    if (this.props.isPreview) {
      const response = await previewRawChartData({
        dataStreams: this.props.dataStreams,
        visoDevices: devices,
        dashboardId: this.props.dashboardId
      })

      data = {
        errors: response.data?.errors || null,
        data: response.data?.data.previewRawChartData
      }
      this.setState({ isChartDataLoading: false })
    } else {
      this.setState({ isChartDataLoading: true })

      if (devices.length) {
        if (isDefaultTime) {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
            timeRange: { start: actualTimeRange.start, end: actualTimeRange.end }
          })
          this.setState({ isChartDataLoading: false })

          data = {
            errors: response.data?.errors || null,
            data: response.data?.data.rawChartData
          }
        } else {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
          })
          this.setState({ isChartDataLoading: false })

          data = {
            errors: response.data?.errors || null,
            data: response.data?.data.rawChartData
          }
        }

      }
    }

    if (data.errors) {
      const errorMessage = data.errors?.[0]?.message
      return this.setState({
        dataErrorMessage: errorMessage,
        validData: false,
        isChartDataLoading: false
      })
    }

    if (data.data) {

      const rawData = data.data.flatMap(item => item.result.data)
      const rawFields = data.data.flatMap(item => item.result.fields)

      const chartData = [rawData[0]]

      this.setState({
        chartData: {
          groupBy: [],
          data: chartData,
          uniqueGroupValues: []
        },
        metricColumns: rawFields,
        dataErrorMessage: '',
        validData: true,
        // isChartDataLoading: false,
        // cartesianConfig: mapChartValuesToCartesianConfig(this.state.chartConfig)
      })//, () => { console.log('After changes: ', this.state) })
    }
  }

  getErrorMessage = () => {
    if (!this.state.validData) {
      return this.state.dataErrorMessage
    }

    return ''
  }

  // Render error message
  renderErrorContext = () => {
    return (
      <div className='chart-error'>
        <div>
          {this.getErrorMessage()}
        </div>
        <div>
          {this.state.dataWarnMessage}
        </div>
      </div>
    )
  }

  hasError = () => {
    const {chartType, chartValues} = this.props;

    if (!this.state.validData) {
      return true
    }

    if(chartType === 'card' || chartType === 'table') {
      return false
    }

    return false
  }

  renderContent = () => {
    const { cardConfig, metricColumns, chartData, validData, dataErrorMessage, chartConfig, dataWarnMessage } = this.state;

    const streamData = this.getOverrideData()

    const fontSize = chartConfig?.fontSize;
    const labelName = this.getLabel();

    return (
      <React.Fragment>
          {this.hasError()
            ?
            this.renderErrorContext()
            :
          <div
            className={classNames('charts-card-chart-content', {
              'charts-card-white-text': this.state.chartConfig?.cardType?.name === 'filledCard'
            })}
            style={this.state.chartConfig?.cardType?.name === 'filledCard'
              ? { backgroundColor: this.getCardColorTierByData() }
              : {}
            }
          >
            <div className='charts-card-chart-content__title'>
              {chartConfig.chartTitle}
            </div>
            <div className={classNames('charts-card-chart-content__main', {
              'charts-card-chart-content__main_small': fontSize === 'Small',
              'charts-card-chart-content__main_large': fontSize === 'Large'
            })}>
              <div style={this.state.chartConfig?.cardType?.name !== 'filledCard' ? { color: this.getCardColorTierByData() } : {}}>
                {chartData.data.length === 0
                  ? 'No data'
                  // : `${cardConfig.prefix} ${this.getDisplayedData()} ${cardConfig.suffix}`
                  : (streamData || `${this.getDisplayedData(chartConfig)}`)
                }
              </div>
              {chartConfig?.cardType?.name === 'indicator' && (
                <div
                  className="charts-card-chart-content__main__indicator-bar"
                  style={{
                    width: '72px',
                    height: '4px',
                    overflow: 'hidden',
                    borderRadius: '999px',
                    display: 'block',
                    margin: '6px auto 2px'
                  }}
                >
                  <div
                    className="charts-card-chart-content__main__indicator-bar__progress"
                    style={{
                      background: this.getCardColorTierByData(),
                      width: `${this.getDataProgressPercentage()}%`, // data total percentage in use 
                      height: '100%',
                      borderRadius: 'inherit'
                    }}
                  />
                </div>
              )}
              {labelName &&
                <div
                  className={classNames('charts-card-chart-content__main__override', {
                    'charts-card-white-text': this.state.chartConfig?.cardType?.name === 'filledCard',
                  })}
                // style={{
                //   color: streamData.color
                // }}
                >
                  {labelName || ''}
                </div>
              }
            </div>
            {dataWarnMessage && <div className='chart-error'>
              {dataWarnMessage}
            </div>
            }
          </div>
        }
      </React.Fragment>
    );
  }

  render() {
    const { color, chart, isLegacy, isEditMode } = this.props;
    const isFilledCard = this.state.chartConfig.cardType.name === 'filledCard'

    return (
      <Card
        noGrid
        color={this.props.color}
        menu={this.state.hasMenu && !isLegacy ? 'dot' : ''}
        chartId={this.props.chart.id}
        contentLoading={this.state.isChartDataLoading}
        className={classNames("overview charts-card-chart", {
          'chart-card-chart-actions-button-white': this.state.chartConfig?.cardType?.name === 'filledCard',
          'chart-card-chart-actions-button': this.state.chartConfig?.cardType?.name !== 'filledCard',
        })}
        height={this.props.isPreview && this.props.height}
        content={this.state.isChartDataLoading ? <ChartLoader color={this.props.color} /> : this.renderContent()}
        onToogleMenu={this.props.onToogleMenu}
        dropDownMenu={this.state.hasMenu && !isLegacy && this.props.onEdit && chartDotMenu({
          isLight: isFilledCard,
          color,
          showAI: this.props.showAI,
          onEdit: this.props.onEdit,
          onCopy: this.props.onCopy,
          onDelete: this.props.onDelete,
          onExplainKPI: () => this.props.onExplainKPI(chart?.id),
          onImproveChart: () => this.props.onImproveChart(chart?.id),
          isEditMode
        })}

      />
    );
  }
}

CardChart.propTypes = {
  onEdit: PropTypes.func,
  model: PropTypes.object,
  chart: PropTypes.any,
  previewParams: PropTypes.any,
  dashboardDevices: PropTypes.any,
  dashboardTimeRange: PropTypes.any,
  height: PropTypes.any,
  isPreview: PropTypes.bool,
  refreshPreview: PropTypes.bool
};

export default CardChart;
